import React, { useEffect, useState } from 'react'
import AdminLayout from '../../../layouts/AdminLayout'
import LogTable from '../components/LogTable'
import useLog from '../../../services/log/hooks/useLog'
import SkeletonTable from '../components/SkeletonTable'
import PaginationComponent from '../../../shared/components/pagination/PaginationComponent'
import { useAuth } from '../../../auth/context/AuthProvider'
import useGetProperty from '../../property-management/hooks/useGetProperty'
import { Card, Form, Row, Col, Button, Alert } from 'react-bootstrap'
import { IconFilterOff, IconSearch } from '@tabler/icons-react'

// Curated "Module" picker — maps a friendly label to the exact model_type
// class string(s) that domain writes under. Kept in one place so it's
// obvious which values to add here whenever a new module starts logging.
const MODULE_OPTIONS: { label: string; modelTypes: string[] }[] = [
    { label: 'Reservation', modelTypes: ['App\\Models\\BookingRoom', 'App\\Models\\V4\\BookingRoom'] },
    { label: 'Room Type', modelTypes: ['App\\Models\\V4\\RoomType'] },
    { label: 'Rate Plan / Link Rate', modelTypes: ['App\\Models\\V4\\RatePlan'] },
    { label: 'Room Maintenance', modelTypes: ['App\\Models\\V4\\RoomMaintenance'] },
    { label: 'Availability', modelTypes: ['App\\Models\\V4\\Availability', 'channex_availability_sync'] },
    { label: 'Restriction', modelTypes: ['App\\Models\\V4\\Restriction', 'channex_restriction_sync'] },
    { label: 'Channex Sync (general)', modelTypes: ['channex_api_call'] },
    { label: 'Dynamic Pricing', modelTypes: ['App\\Models\\V4\\DynamicPricingGroup', 'App\\Models\\V4\\DynamicPricingRule', 'App\\Models\\V4\\DynamicPricingPeriod'] },
    { label: 'Inventory', modelTypes: ['App\\Models\\V4\\Inventory', 'App\\Models\\V4\\InventoryCategory'] },
    { label: 'Property & Access', modelTypes: ['App\\Models\\V4\\Property', 'App\\Models\\Property', 'App\\Models\\V4\\User'] },
    { label: 'Agent', modelTypes: ['App\\Models\\Agent'] },
    { label: 'Property Bank', modelTypes: ['App\\Models\\PropertyBank'] },
    { label: 'Imports', modelTypes: ['App\\Models\\V4\\RateImport', 'booking_import'] },
];

const columnsLog = [
    { label: "Date (GMT+8)", key: "created_at" },
    { label: "User", key: "user.name" },
    { label: "Invoice Number", key: "invoice_number" },
    { label: "Voucher Number", key: "voucher_number" },
    { label: "Action", key: "action" },
    { label: "Detail", key: "log" },
    { label: "Changes", key: "diff" },
];

const LogPages: React.FC = () => {
    const { user } = useAuth();

    const [search, setSearch] = useState('');
    const [moduleLabel, setModuleLabel] = useState('');
    // Set only via the detail drawer's "View all related actions" — not a
    // manual input, since correlation ids aren't something a user types.
    const [correlationId, setCorrelationId] = useState('');

    const [currentPage, setCurrentPage] = useState<number>(1);
    // No UI to change this yet — kept as a plain constant rather than state
    // with an unused setter.
    const perPage = 10;
    const [currentProperty, setCurrentProperty] = useState<string>('');

    useEffect(() => {
        if (user && user.active_property_id) {
            setCurrentProperty(user.active_property_id);
        } else {
            setCurrentProperty('');
        }
    }, [user]);

    // Any filter change should land the user back on page 1 — staying on,
    // say, page 4 of a now much-smaller filtered result just shows "no data"
    // and looks broken.
    useEffect(() => {
        setCurrentPage(1);
    }, [search, moduleLabel, correlationId]);

    const selectedModule = MODULE_OPTIONS.find((option) => option.label === moduleLabel);
    const hasActiveFilters = Boolean(search || moduleLabel || correlationId);

    const { data: logData, isFetching: isFetchingLog } = useLog({
        include: "user",
        page: currentPage,
        limit: perPage,
        search: search,
        filters: {
            property_id: currentProperty,
            model_type: selectedModule?.modelTypes,
            correlation_id: correlationId,
        }
    });

    const { data: property } = useGetProperty(currentProperty);

    const clearFilters = () => {
        setSearch('');
        setModuleLabel('');
        setCorrelationId('');
    };

    return (
        <AdminLayout>
            <div className='container-fluid mt-4'>

                {/* ======= Title ======= */}
                <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-3">
                    <div>
                        <h3 className="mb-1">Activity Logs</h3>
                        <p className="text-muted mb-0">
                            Every recorded action for <strong>{property ? property.data.name : 'this property'}</strong> — who did what, and when.
                        </p>
                    </div>
                </div>

                {correlationId && (
                    <Alert variant="info" className="d-flex justify-content-between align-items-center">
                        <span>Showing every action linked to one correlation id — the full chain of a single event.</span>
                        <Button variant="outline-secondary" size="sm" onClick={() => setCorrelationId('')}>Clear</Button>
                    </Alert>
                )}

                {/* ======= Filter Section ======= */}
                <Card className="mb-4">
                    <Card.Body>
                        <Row className="g-3 align-items-end">
                            <Col md={7}>
                                <Form.Label className="small text-muted mb-1">Search</Form.Label>
                                <div className="input-icon">
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Keyword, invoice, voucher..."
                                        value={search}
                                        onChange={(e) => setSearch(e.target.value)}
                                    />
                                    <span className="input-icon-addon">
                                        <IconSearch size={16} />
                                    </span>
                                </div>
                            </Col>
                            <Col md={4}>
                                <Form.Label className="small text-muted mb-1">Module</Form.Label>
                                <Form.Select
                                    value={moduleLabel}
                                    onChange={(e) => setModuleLabel(e.target.value)}
                                >
                                    <option value="">All modules</option>
                                    {MODULE_OPTIONS.map((option) => (
                                        <option key={option.label} value={option.label}>{option.label}</option>
                                    ))}
                                </Form.Select>
                            </Col>
                            <Col md={1} className="d-flex">
                                <Button
                                    variant="outline-secondary"
                                    className="w-100"
                                    disabled={!hasActiveFilters}
                                    onClick={clearFilters}
                                    title="Clear all filters"
                                >
                                    <IconFilterOff size={16} />
                                </Button>
                            </Col>
                        </Row>
                    </Card.Body>
                </Card>

                {/* ======= Table Section ======= */}
                {isFetchingLog ? (
                    <SkeletonTable rows={10} columns={columnsLog.length} />
                ) : logData && logData?.data.length > 0 ? (
                    <>
                        {logData.pagination && (
                            <p className="text-muted small mb-2">
                                Showing {logData.pagination.from ?? 0}–{logData.pagination.to ?? 0} of {logData.pagination.total} actions
                            </p>
                        )}
                        <LogTable data={logData} columns={columnsLog} onViewRelated={setCorrelationId} />
                    </>
                ) : (
                    <div className="text-center py-5 text-muted">
                        <h5>No activity found</h5>
                        <p className="mb-0 small">
                            {hasActiveFilters ? 'Try adjusting or clearing the filters above.' : 'Nothing has been recorded yet for this property.'}
                        </p>
                    </div>
                )}

                {/* ======= Pagination Section ======= */}
                {logData && logData?.pagination && logData.data.length > 0 && (
                    <PaginationComponent
                        totalPages={logData.pagination.last_page}
                        currentPage={currentPage}
                        onPageChange={setCurrentPage}
                    />
                )}
            </div>
        </AdminLayout>
    )
}

export default LogPages
